import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fetchContentGita, fetchContentGurus, fetchContentGitaIndex } from "@/lib/content/content-service";
import { GitaVerseCard } from "./GitaVerseCard";
import { GitaChapterNav } from "./GitaChapterNav";

export function GitaPage({ token }) {
  const { id } = useParams();
  const [gita, setGita] = useState(null);
  const [gitaIndexEntry, setGitaIndexEntry] = useState(null);
  const [gurusById, setGurusById] = useState({});
  const [activeChapter, setActiveChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      fetchContentGita(id),
      fetchContentGitaIndex().catch(() => null),
      fetchContentGurus().catch(() => ({ gurus: [] })),
    ])
      .then(([gitaRes, indexRes, guruRes]) => {
        setGita(gitaRes.gita);
        const entry = indexRes?.gitas?.find((g) => g.id === id) ?? null;
        setGitaIndexEntry(entry);
        const map = {};
        for (const g of guruRes.gurus || []) map[g.id] = g;
        setGurusById(map);
        const first = gitaRes.gita?.chapters?.[0]?.chapter ?? entry?.chapters?.[0]?.chapter ?? null;
        setActiveChapter(first);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const chapterNav = useMemo(() => {
    if (gitaIndexEntry?.chapters?.length) return gitaIndexEntry.chapters;
    return (gita?.chapters ?? []).map((ch) => ({
      chapter: ch.chapter,
      title: ch.title,
      verse_count: ch.verses?.length ?? 0,
    }));
  }, [gita, gitaIndexEntry]);

  const scrollToChapter = (chapterNum) => {
    setActiveChapter(chapterNum);
    document.getElementById(`gita-chapter-${chapterNum}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!gita) {
    return (
      <div className="min-h-screen py-24 px-4 text-center">
        <p className="text-muted-foreground mb-4">{error || "Gītā not found."}</p>
        <Button asChild variant="outline">
          <Link to="/gitas">Back to Gītās</Link>
        </Button>
      </div>
    );
  }

  const declared = gita.chapter_count_declared ?? gitaIndexEntry?.chapter_count_declared;
  const loaded = gita.chapters?.length ?? gitaIndexEntry?.chapter_count_loaded ?? 0;
  const verseTotal = gita.verse_count ?? gitaIndexEntry?.verse_count;

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid={`gita-page-${id}`} data-gita-mode="shastra">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/gitas">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Gītās
          </Link>
        </Button>
        <Card className="bg-white/90 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-3xl">{gita.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{gita.source}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {declared != null ? `${loaded} of ${declared} chapters loaded` : `${loaded} chapters`}
              {verseTotal != null ? ` · ${verseTotal} verses` : ""}
              {(gita.languages_supported ?? gitaIndexEntry?.languages_supported)?.length
                ? ` · ${(gita.languages_supported ?? gitaIndexEntry.languages_supported).slice(1).join(", ")}`
                : ""}
            </p>
            <p className="text-xs text-muted-foreground/80 mt-2" data-testid="gita-shastra-notice">
              Śāstra mode: śloka, meaning, and context only — no mantra suggestions or japa on Gītā pages.
              For deeper practice, seek guidance from a living Guru.
            </p>
          </CardHeader>
        </Card>

        <GitaChapterNav chapters={chapterNav} activeChapter={activeChapter} onSelect={scrollToChapter} />

        <div className="space-y-8">
          {(gita.chapters ?? []).map((ch) => (
            <section key={ch.chapter} id={`gita-chapter-${ch.chapter}`} data-testid={`gita-chapter-${ch.chapter}`}>
              <h2 className="font-heading text-xl mb-4">
                Chapter {ch.chapter}: {ch.title}
              </h2>
              <div className="space-y-3">
                {(ch.verses ?? []).map((v) => (
                  <GitaVerseCard key={v.verse} verse={v} gurusById={gurusById} token={token} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
